import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    Typography,
    Stack,
    Dialog,
    Button,
    } from "../ui";
import { useLocation } from "react-router-dom";
import {
  INITIAL_BASE_URL,
  isEmpty,
  printDebugLog,
  SUCCESS
} from "../../constants/constant";
import axios from "axios";
import Loader from "../global/loader/Loader";
import "./GoldPdp.css";

const AutoCarousel = ({ images = [], interval = 2500, handleClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (images.length === 0) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, interval);
        return () => clearInterval(timer);
    }, [images, interval]);

    if (images.length === 0) return null;

    const handleImageClick = (index) => {
        if (handleClick) handleClick(images[index]);
    };

    return (
        <div className="carousel-container">
            {images.map((img, index) => (
                <img
                    key={index}
                    onClick={() => handleImageClick(index)}
                    src={`${INITIAL_BASE_URL.BASE_IMAGE_URL}${img}`}
                    alt={`Product ${index + 1}`}
                    className={`carousel-image ${index === currentIndex ? "active" : ""}`}
                />
            ))}
        </div>
    );
};

const GoldPdp = () => {

    const INITIAL_SHOW = {
        show: false, dataSrc: "",
    }

    const location = useLocation();

    const [details, setDetails] = useState({});
    const [shopData, setShopData] = useState({});
    const [imageArr, setImageArr] = useState([]);
    const [logo, setLogo] = useState("");
    const [gifPath, setGifPath] = useState("");
    const [loader, setLoader] = useState(false);
    const [showImageOnDialog, setShowImageOnDialog] = useState(INITIAL_SHOW);

    const getProductDetails = async (id, shopHash) => {
        try {
            setLoader(true);
            const cartUrl = `${INITIAL_BASE_URL.BACKEND_BASE_URL}gold/get-web-gold-record?id=${id}&shopHash=${shopHash}`;
            printDebugLog("getProductDetails", cartUrl);
            const response = await axios.get(cartUrl);
            if (response.data.status === SUCCESS) {
                const payLoad = response.data.jcJson;
                setDetails(payLoad.details);
                setShopData({
                    shopName: payLoad.shopName,
                    billIcon: payLoad.billIcon,
                });
                setLogo(payLoad.logo);
                let images = payLoad.imageArr || [];
                if (!isEmpty(payLoad.gifPath)) {
                    images = [...images, payLoad.gifPath];
                    setShowImageOnDialog({
                        show: true,
                        dataSrc: payLoad.gifPath,
                    });
                }

                setImageArr(images);
                setGifPath(payLoad.gifPath);
            }
        } catch (error) {
            console.error("getProductDetails", error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const id = searchParams.get("id");
        const shopHash = searchParams.get("shopHash");
        getProductDetails(id, shopHash);
    }, [location]);

    useEffect(() => {
        const blockShortcuts = (e) => {
            if (
                e.key === "F12" ||
                (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key.toUpperCase())) ||
                (e.ctrlKey && e.key.toUpperCase() === "U")
            ) {
                e.preventDefault();
            }
        };
        const handleVisibility = () => {
            if (document.hidden) {
                document.body.classList.add("hide-sensitive");
            } else {
                document.body.classList.remove("hide-sensitive");
            }
        };

        const handleContextMenu = (e) => e.preventDefault();
        document.addEventListener("visibilitychange", handleVisibility);
        document.addEventListener("keydown", blockShortcuts);
        document.addEventListener("contextmenu", handleContextMenu);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibility);
            document.removeEventListener("contextmenu", handleContextMenu);
            document.removeEventListener("keydown", blockShortcuts);
        }
    }, []);


    return (
        <div className="app-container"
            onContextMenu={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            onSelectStart={(e) => e.preventDefault()}
        >
            {loader && <Loader />}

            {/* Navbar */}
            <header className="navbar">
                <div className="navbar-left">
                    {!isEmpty(shopData?.billIcon ?? "") && (
                        <img
                            src={`${INITIAL_BASE_URL.BASE_IMAGE_URL}${shopData?.billIcon}`}
                            alt="Shop"
                            onClick={() => setShowImageOnDialog({
                                show: true, dataSrc: shopData?.billIcon,
                            })}
                            className="navbar-icon"
                        />
                    )}
                    <span>{shopData?.shopName ?? ""}</span>
                </div>
            </header>

            {/* Main Content */}
            <div className="content-container">
                {/* Image Section */}
                <div className="image-section">
                    <AutoCarousel images={imageArr} interval={2500} handleClick={(img) => setShowImageOnDialog({
                        show: true, dataSrc: img,
                    })} />
                    {!isEmpty(logo) && (
                        <img
                            src={`${INITIAL_BASE_URL.BASE_IMAGE_URL}${logo}`}
                            alt="Logo"
                            onClick={() => setShowImageOnDialog({
                                show: true, dataSrc: logo,
                            })}
                            className="overlay-logo"
                        />
                    )}
                </div>

                {/* Details Section */}
                <div className="details-section">
                    <Stack spacing={2}>
                        {Object.entries(details).map(([key, value]) => {
                            const isSupplier = key === "Supplier Details";
                            return (
                                <Card
                                    key={key}
                                    className={`detail-card ${isSupplier ? "supplier-card" : ""}`}
                                >
                                    <CardContent>
                                        {isSupplier && <div className="verified-ribbon">Verified</div>}
                                        <Typography variant="h6" className="detail-label">
                                            {key}
                                        </Typography>
                                        <Typography
                                            style={{
                                                whiteSpace: "pre-wrap",
                                                overflowWrap: "break-word",
                                            }}
                                            fontFamily="monospace" variant="body1">{value}</Typography>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                </div>

            </div>

            {/* GIF Dialog */}
            <Dialog open={showImageOnDialog.show} fullScreen className="dialog-box">
                <div className="gif-container">
                    <img
                        src={`${INITIAL_BASE_URL.BASE_IMAGE_URL}${showImageOnDialog.dataSrc}`}
                        alt="Promotion"
                        className="zoomed-image"
                    />
                    <Button
                        variant="contained"
                        color="error"
                        className="dismiss-btn"
                        onClick={() => setShowImageOnDialog(INITIAL_SHOW)}
                    >
                        Dismiss
                    </Button>
                </div>
            </Dialog>
        </div>
    );
};

export default GoldPdp;
